import React, { useState } from 'react';
import { Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * SessionLockOverlay - shown after 15 min inactivity
 * SessionExpiredOverlay - shown after 30 min inactivity
 */
export const SessionLockOverlay = () => {
  const { user, unlockSession, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleUnlock = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setLoading(true);
    setError('');
    const { error: unlockError } = await unlockSession(password);
    if (unlockError) {
      setError('Mot de passe incorrect. Veuillez réessayer.');
    }
    setLoading(false);
    setPassword('');
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-gray-900/95 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-orange-600 dark:text-orange-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Session verrouillée</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Votre session a été verrouillée après 15 minutes d'inactivité.
          </p>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-2">
            {user?.email}
          </p>
        </div>

        <form onSubmit={handleUnlock} className="space-y-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              placeholder="Entrez votre mot de passe"
              className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !password}
            className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-semibold rounded-lg transition-colors"
          >
            {loading ? 'Vérification...' : 'Déverrouiller'}
          </button>

          <button
            type="button"
            onClick={signOut}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            Se déconnecter et changer de compte
          </button>
        </form>
      </div>
    </div>
  );
};

export const SessionExpiredOverlay = () => {
  const { setSessionExpired } = useAuth();

  const handleGoToLogin = () => {
    setSessionExpired(false);
    window.location.href = '/admin-login';
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-gray-900/95 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4 text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Session expirée</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Votre session a expiré pour raison de sécurité après 30 minutes d'inactivité.
        </p>
        <button
          onClick={handleGoToLogin}
          className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
        >
          Se reconnecter
        </button>
      </div>
    </div>
  );
};
